import { AutomatedTriggerType, UpfrontPaymentClientType, UpfrontPaymentOption } from '../../graphQL/generated/enums'
import { $RootStore } from '../../src/stores/RootStore'
import moment from 'moment-timezone'
import { action, computed, observable } from 'mobx'

const clientTypes = [
  {
    label: 'New clients',
    type: UpfrontPaymentClientType.New,
  }, {
    label: 'Existing clients',
    type: UpfrontPaymentClientType.Existing,
  },
]

export type BookingItem = {
  description: string
  amount: string
  color?: 'danger' | 'green'
  type?: 'body' | 'bodyBold'
} | 'horizontalLine'

export type CTAItem = {
  title: string
  text: string
  buttonText: string
}

export class EmailPreviewHelper {
  store: $RootStore
  @observable accessor triggerType = AutomatedTriggerType.TrBookingAccepted

  constructor(store: $RootStore) {
    this.store = store
  }

  @action setTriggerType(triggerType: AutomatedTriggerType) {
    this.triggerType = triggerType
  }

  @computed get depositCTA() {
    const { userStore, i18n: { s, currencyFormat } } = this.store
    const { data: { professionalProfile }, onlinePaymentsEnabled } = userStore
    const { clientDepositSettings } = professionalProfile.saasSettings

    if (![
      AutomatedTriggerType.TrBookingRequested,
      AutomatedTriggerType.TrBookingDepositReminder,
    ].includes(this.triggerType)) return null

    const clientDepositSettingsComputed = clientTypes.map(({ type }) => {
      const existing = clientDepositSettings?.find(setting => setting.clientType === type)

      return existing ?? {
        clientType: type,
        depositRequired: UpfrontPaymentOption.None,
        depositAmount: 0,
      }
    })

    const approvalDeposit = clientDepositSettingsComputed.find(setting => setting.depositRequired === UpfrontPaymentOption.Approval)

    if (approvalDeposit) {
      let text = ''
      if (!!approvalDeposit.useFixedAmount) {
        text = `A ${currencyFormat(approvalDeposit.depositAmount)} deposit is required in advance of the booking.`
      } else {
        text = `A ${approvalDeposit.depositAmount}% deposit is required in advance of the booking.`
      }

      if (onlinePaymentsEnabled) {
        return {
          title: s('Deposit due'),
          text: s(text),
          buttonText: s('Pay now via card'),
        }
      }
    }

    return null
  }

  @computed get consultationCTA() {
    const { i18n: { s } } = this.store

    if ([
      AutomatedTriggerType.TrBookingConsultationFormReminder,
      AutomatedTriggerType.TrBookingAccepted,
      AutomatedTriggerType.TrBookingUpdated,
    ].includes(this.triggerType)) {
      return {
        title: s('Consultation form'),
        text: s('You are required to complete a consultation form before your booking.'),
        buttonText: s('Complete consultation form'),
      }
    }

    return null
  }

  @computed get cancellationText() {
    const { userStore, i18n: { s } } = this.store
    const { data: { professionalProfile } } = userStore
    const { defaultCancellationText } = professionalProfile.saasSettings

    return defaultCancellationText ?? s('No cancellation policy has been set yet.')
  }

  @computed get bookingItems() {
    const { externalTreatmentStore, i18n: { s, currencyFormat }, externalPromocodeStore } = this.store

    if (this.triggerType === AutomatedTriggerType.GcPaymentReceipt) {
      return {
        items: [{
          description: s('Gift card'),
          amount: currencyFormat(1000),
        }],
        title: s('Payment details'),
      }
    }

    if (this.triggerType === AutomatedTriggerType.GcRedemptionCode) {
      return {
        items: [{
          description: s('Gift card amount'),
          amount: currencyFormat(1000),
        }],
        title: '',
      }
    }

    if (this.triggerType === AutomatedTriggerType.TrPaymentReceipt) {
      return {
        items: [{
          description: s('Deposit'),
          amount: currencyFormat(1000),
        }],
        title: s('Payment details'),
      }
    }

    const items: Array<BookingItem> = []
    let totalTreatmentFee = 0

    if (externalTreatmentStore.items.length > 0) {
      const treatment = externalTreatmentStore.items[0]
      items.push({
        description: treatment.data.name,
        amount: currencyFormat(treatment.data.price),
      })

      totalTreatmentFee += treatment.data.price
    } else {
      items.push({
        description: s('Treatment'),
        amount: currencyFormat(1000),
      })

      totalTreatmentFee += 1000
    }

    items.push('horizontalLine')

    items.push({
      description: s('Total'),
      amount: currencyFormat(totalTreatmentFee),
      type: 'bodyBold',
    })

    let totalFee = totalTreatmentFee

    if (externalPromocodeStore.items.length > 0) {
      const { discountType, discountAmount, code } = externalPromocodeStore.items[0].data

      const percentDiscount = totalTreatmentFee * (discountAmount ?? 1) / 100
      const totalDiscount = discountType === 'percent' ? percentDiscount : (discountAmount ?? 0)
      const safeDiscount = Math.min(totalDiscount, totalFee)

      items.push({
        description: s(`Discount ${code}`),
        amount: currencyFormat(-safeDiscount),
        type: 'bodyBold',
        color: 'green',
      })

      totalFee -= safeDiscount
    } else {
      const safeDiscount = Math.min(500, totalFee)

      items.push({
        description: s('Discount FIVEOFF'),
        amount: currencyFormat(-safeDiscount),
        type: 'bodyBold',
        color: 'green',
      })

      totalFee -= safeDiscount
    }

    items.push('horizontalLine')

    let amountPaid = 0
    const depositTotal = 100

    if ([
      AutomatedTriggerType.TrBookingCancellationRequested,
      AutomatedTriggerType.TrBookingCancelled,
      AutomatedTriggerType.TrBookingCompleted,
    ].includes(this.triggerType)) {
      amountPaid = depositTotal
    }

    const totalDepositDue = depositTotal - amountPaid
    const totalDue = totalFee - amountPaid

    if ([
      AutomatedTriggerType.TrBookingCancellationRequested,
      AutomatedTriggerType.TrBookingCancelled,
    ].includes(this.triggerType)) {
      items.push({
        description: s('Deposit paid'),
        amount: currencyFormat(amountPaid),
        type: 'bodyBold',
      })
    }

    if ([
      AutomatedTriggerType.TrBookingCompleted,
      AutomatedTriggerType.TrBookingAccepted,
      AutomatedTriggerType.TrBookingRequested,
    ].includes(this.triggerType) && amountPaid > 0) {
      items.push({
        description: s('Deposit paid'),
        amount: currencyFormat(-amountPaid),
        type: 'bodyBold',
      })
    }

    if (depositTotal > 0 && this.triggerType === AutomatedTriggerType.TrBookingAccepted) {
      items.push({
        description: s('Deposit due now'),
        amount: currencyFormat(totalDepositDue),
        type: 'bodyBold',
        color: 'danger',
      })
    }

    if (depositTotal > 0 && this.triggerType === AutomatedTriggerType.TrBookingRequested) {
      items.push({
        description: s('Deposit due'),
        amount: currencyFormat(totalDepositDue),
        type: 'bodyBold',
        color: 'danger',
      })
    }

    if ([
      AutomatedTriggerType.TrBookingAccepted,
      AutomatedTriggerType.TrBookingRequested,
    ].includes(this.triggerType) && totalFee > depositTotal) {
      const leftToPay = amountPaid > depositTotal ? (totalFee - amountPaid) : (totalFee - depositTotal)
      items.push({
        description: s('To pay at booking'),
        amount: currencyFormat(leftToPay),
        type: 'bodyBold',
      })
    }

    if (this.triggerType === AutomatedTriggerType.TrBookingCompleted) {
      items.push({
        description: s('Final payment due'),
        amount: currencyFormat(totalDue),
        type: 'bodyBold',
      })
    }

    let stripeRefundAmount = 0

    if (this.triggerType === AutomatedTriggerType.TrBookingCancelled) {
      stripeRefundAmount = 1000
    }

    if (stripeRefundAmount > 0) {
      items.push({
        description: s('Refund'),
        amount: currencyFormat(stripeRefundAmount),
        color: 'green',
        type: 'bodyBold',
      })
    }

    return { items, title: s('Booking details') }
  }

  @computed get declineReason() {
    const { i18n: { s } } = this.store

    return s("I'm not available at this time / I'm unable to travel to your location.")
  }

  @computed get finalPaymentCTA() {
    const { i18n: { s, currencyFormat } } = this.store

    if (this.triggerType === AutomatedTriggerType.TrBookingCompleted) {
      return {
        title: s('Final payment due'),
        text: s(`Final payment of ${currencyFormat(1000)} is due today.`),
        buttonText: s('Pay now via card'),
      }
    }

    return null
  }

  @computed get dateAndLocation() {
    const { userStore, i18n: { s }, externalAddressStore } = this.store
    const { data: { professionalProfile } } = userStore
    const { mobileLocation, staticLocations } = professionalProfile.saasSettings

    const address = {
      line1: '123 Fake Street',
      line2: 'Fakeville',
      postalCode: 'FA1 2KE',
    }

    let locationName = ''

    if (mobileLocation && !!mobileLocation.enabled) {
      locationName = s('Your address of choice')
    } else if (staticLocations && staticLocations.length > 0 && !!staticLocations[0].enabled) {
      locationName = staticLocations[0].name
      const extAddress = externalAddressStore.findItemById(staticLocations[0].externalAddressId || '')
      if (extAddress) {
        address.line2 = extAddress.data.line2
        address.postalCode = extAddress.data.postalCode
      }
    }

    const date = moment().add(1, 'day').set('minutes', 0).toDate()
    const time = `${moment(date).format('LT')} - ${moment(date).add(60, 'minutes').format('LT')}`

    return {
      date: moment(date).format('dddd Do MMMM YYYY'),
      time: `${time}${locationName === s('Your address of choice') ? '+ 10 min packdown' : ''}`,
      locationName,
      locationAddress: [address?.line1, address?.line2, address?.postalCode].filter(a => Boolean(a)).join(', '),
    }
  }

  @computed get ctas() {
    const depositCta = this.depositCTA
    const consultationCta = this.consultationCTA
    const finalPaymentCta = this.finalPaymentCTA

    return [
      depositCta,
      consultationCta,
      finalPaymentCta,
    ].filter(Boolean) as Array<CTAItem>
  }
}